import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogIn, Eye, EyeOff } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

const PartnerLoginPage = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [loginSuccess, setLoginSuccess] = useState(false);
  const [partnerId, setPartnerId] = useState(null);

  // Handle navigation after successful login
  useEffect(() => {
    if (loginSuccess && partnerId) {
      // Small delay to ensure localStorage is set
      const timer = setTimeout(() => {
        navigate(`/portal/dashboard?partner_id=${partnerId}`, { replace: true });
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loginSuccess, partnerId, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/partner-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Login fehlgeschlagen');
      }

      // Store JWT token AND partner data
      if (data.token) {
        localStorage.setItem('partner_token', data.token);
      }
      localStorage.setItem('partner_id', data.partner_id);
      localStorage.setItem('partner_email', data.email);
      if (data.company_name) {
        localStorage.setItem('partner_company', data.company_name);
      }

      // Trigger navigation via useEffect
      setPartnerId(data.partner_id);
      setLoginSuccess(true);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <main className="pt-32 pb-16">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white border-2 border-gray-100 rounded-3xl p-10 shadow-xl">
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Partner-Login</h1>
              <p className="text-gray-600">Zugang zum Partner-Portal</p>
            </div>

            {error && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">E-Mail</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                  placeholder="partner@firma.de"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">Passwort</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full px-4 py-4 border-2 border-gray-200 rounded-xl shadow-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green"
                    placeholder="••••••••"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-green hover:bg-brand-green-dark text-white py-4 rounded-xl font-semibold inline-flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                {loading ? 'Anmelden...' : 'Anmelden'}
                <LogIn className="w-5 h-5" />
              </button>
              
              <div className="text-center mt-3">
                <a href="/partner/passwort-vergessen" className="text-sm text-gray-600 hover:text-brand-green">
                  Passwort vergessen?
                </a>
              </div>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600">
                Noch kein Account?{' '}
                <a href="/partner/abo" className="text-brand-green hover:text-brand-green-dark font-semibold underline">
                  Jetzt Partner werden
                </a>
              </p>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default PartnerLoginPage;
